// SupportServices.js
import React from 'react';
import { motion } from 'framer-motion';
import { Globe, BookOpen, Calendar, FileText, FolderOpen, CheckCircle2 } from 'lucide-react';

const SupportServices = () => {
  const services = [
    {
      icon: Globe,
      title: 'Country & Institution Shortlisting',
      description: 'Personalized recommendations based on your academic profile and career goals',
      color: 'blue'
    },
    {
      icon: BookOpen,
      title: 'Course Suitability & Intake Guidance',
      description: 'Expert advice on program selection and optimal application timelines',
      color: 'purple'
    },
    {
      icon: Calendar,
      title: 'Application Planning & Timeline Management',
      description: 'Structured roadmap to keep your applications on track',
      color: 'green'
    },
    {
      icon: FileText,
      title: 'Statement of Purpose Preparation',
      description: 'Professional refinement of your personal statement to meet institutional standards',
      color: 'orange'
    },
    {
      icon: FolderOpen,
      title: 'Document Organisation & Submission',
      description: 'Complete support to ensure all materials are properly prepared and submitted',
      color: 'red'
    },
    {
      icon: CheckCircle2,
      title: 'Admission Follow-ups & Next Steps',
      description: 'Ongoing guidance from application to acceptance and beyond',
      color: 'teal'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'border-t-blue-500',
      purple: 'border-t-purple-500',
      green: 'border-t-green-500',
      orange: 'border-t-orange-500',
      red: 'border-t-red-500',
      teal: 'border-t-teal-500'
    };
    return colors[color] || 'border-t-blue-500';
  };

  return (
    <section className="py-16 bg-[#F5F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            Our Study Abroad Support
          </h2>
          <p className="text-gray-600 text-sm font-light max-w-2xl mx-auto">
            We provide comprehensive guidance throughout the admissions journey. We focus on preparation, accuracy, and clarity.
          </p>
        </motion.div>

        {/* Service Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`bg-white p-6 border-t-2 ${getColorClasses(service.color)} hover:shadow-lg transition-shadow`}
              >
                <Icon size={32} className="text-gray-700 mb-4" />
                
                <h3 className="text-lg font-semibold text-[#202356] mb-3 leading-tight">
                  {service.title}
                </h3>
                
                <p className="text-gray-600 text-sm font-light leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SupportServices;
